import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/core';
import styles from '../../styles';
import Sidebar from '../../components/layout/Sidebar';
import MobileSidebarNav from './MobileSidebarNav';
import SidebarToggleIcon from '../../components/SidebarToggleIcon/SidebarToggleIcon';
import { FooterPager } from '../../components/FooterPager/FooterPager';

import { CopySVG, CheckSVG } from '../../components/Icon/Icon';

const MarkdownTemplate = ({
  section,
  sidebarCategories,
  initialSidebarCategory,
  html,
  markdown,
}) => {
  const [copied, setCopied] = useState(false);
  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(markdown);
      setCopied(true);

      setTimeout(() => {
        setCopied(false);
      }, 1000);
    } catch (err) {
      console.error('Error', err);
    }
  };

  return (
    <div className='container'>
      <div id='main-content' className='row'>
        <div className='col-md-4 col-lg-3' css={style.sidebarWrap}>
          <SidebarToggleIcon />
          <DesktopSidebar
            section={section}
            categories={sidebarCategories}
            initialCategory={initialSidebarCategory}
          />
          <MobileSidebar
            section={section}
            categories={sidebarCategories}
            initialCategory={initialSidebarCategory}
          />
        </div>
        <div className='col-md-8 col-lg-9' css={style.contentWrap}>
          <button onClick={copyToClipboard} css={style.button}>
            {copied ? <CheckSVG /> : <CopySVG />}
            Copy Markdown
          </button>
          <div className='s-post' css={style.content} dangerouslySetInnerHTML={{ __html: html }} />
          <FooterPager />
        </div>
      </div>
    </div>
  );
};

MarkdownTemplate.propTypes = {
  section: PropTypes.string.isRequired,
  sidebarCategories: PropTypes.array.isRequired,
  initialSidebarCategory: PropTypes.string,
  html: PropTypes.string.isRequired,
};

const style = {
  contentWrap: css`
    background: ${styles.color.white};
    position: relative;
    @media (min-width: ${styles.breakpoint.md}) {
      border-left: 1px solid ${styles.border.color.grey};
    }

    #main-content[data-toggle='collapsed'] & {
      width: 100%;
      flex: 1;
      max-width: 100%;
    }
  `,
  sidebarWrap: css`
    #main-content[data-toggle='collapsed'] & {
      flex: 0;
    }
    #main-content[data-toggle='collapsed'] & div {
      display: none;
    }
  `,
  button: css`
    padding: 1rem 2rem;
    border-radius: 6px;
    font-weight: 300;
    border: none;
    font-size: 16px;
    cursor: pointer;
    color: #5f5f61;
    position: absolute;
    top: 20px;
    right: 0%;
    display: flex;
    align-items: center;
    gap: 8px;
    transition: background 0.5s;
    border-radius: 8px;

    @media (max-width: ${styles.breakpoint.md}) {
      font-size: 14px;
    }

    &:hover {
      background: #f8f7fc;
    }
  `,
  content: css`
    @media (min-width: ${styles.breakpoint.md}) {
      padding: ${styles.spacing.xs};
    }
  `,
};

const DesktopSidebar = ({ section, categories, initialCategory }) => (
  <div id='doc-sidebar' css={desktopSidebarStyle.root}>
    <div css={desktopSidebarStyle.sidebar}>
      <Sidebar section={section} categories={categories} initialCategory={initialCategory} />
    </div>
  </div>
);

const desktopSidebarStyle = {
  root: css`
    height: 100%;
    position: relative;
    display: none;
    @media (min-width: ${styles.breakpoint.md}) {
      display: block;
    }
  `,
  sidebar: css`
    padding: ${styles.spacing.md} 0 21rem;
    position: sticky;
    top: 7.2rem;
    overflow-y: auto;
  `,
};

const MobileSidebar = ({ section, categories, initialCategory }) => {
  const [isOpen, setIsOpen] = useState(false);

  const _handleClick = () => setIsOpen((state) => !state);

  const _handleItemClick = () => setIsOpen(false);

  return (
    <MobileSidebarNav isOpen={isOpen} onClick={_handleClick}>
      <Sidebar
        section={section}
        categories={categories}
        initialCategory={initialCategory}
        onItemClick={_handleItemClick}
      />
    </MobileSidebarNav>
  );
};

export default MarkdownTemplate;
